/**
 * Permissões Service - Epic 27: Controle de Acesso
 *
 * Serviço para gestão de Permissões e Controle de Acesso
 * Endpoints: Telas, Matriz de Permissões, Permissões de Usuário
 */
import apiClient from './apiClient';
import type { Permissao } from './perfisService';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

export interface Tela {
  id: number;
  codigo: string;
  nome: string;
  descricao: string;
  modulo: string;
  url_frontend: string;
  url_api: string;
  ordem_menu: number;
  icone: string;
  ativo: boolean;
}

export interface PermissaoAtualizar {
  pode_visualizar: boolean;
  pode_criar: boolean;
  pode_editar: boolean;
  pode_excluir: boolean;
  pode_exportar: boolean;
}

export interface PermissaoDetalhada {
  perfil_id: number;
  perfil_codigo: string;
  perfil_nome: string;
  tela_id: number;
  tela_codigo: string;
  tela_nome: string;
  pode_visualizar: boolean;
  pode_criar: boolean;
  pode_editar: boolean;
  pode_excluir: boolean;
  pode_exportar: boolean;
}

export interface MatrizPermissoes {
  perfis: Array<{
    id: number;
    codigo: string;
    nome: string;
    nivel_acesso: number;
  }>;
  telas: Array<{
    id: number;
    codigo: string;
    nome: string;
    modulo: string;
    ordem_menu: number;
  }>;
  matriz: PermissaoDetalhada[];
}

export interface PermissaoEfetiva {
  tela_id: number;
  tela_codigo: string;
  tela_nome: string;
  modulo: string;
  pode_visualizar: boolean;
  pode_criar: boolean;
  pode_editar: boolean;
  pode_excluir: boolean;
  pode_exportar: boolean;
  visivel_menu: boolean; // Epic 27 - Story 5: Controle de visibilidade no menu
  origem: 'perfil' | 'usuario'; // Indica se vem do perfil ou é override do usuário
}

export interface UsuarioPermissaoInput {
  usuario_id: number;
  tela_id: number;
  pode_visualizar: boolean;
  pode_criar: boolean;
  pode_editar: boolean;
  pode_excluir: boolean;
  pode_exportar: boolean;
}

export interface UsuarioPermissao {
  id: number;
  usuario_id: number;
  usuario_username: string;
  tela_id: number;
  tela_codigo: string;
  tela_nome: string;
  pode_visualizar: boolean;
  pode_criar: boolean;
  pode_editar: boolean;
  pode_excluir: boolean;
  pode_exportar: boolean;
  concedido_por: string | null;
  data_concessao: string;
}

// ============================================================================
// SERVIÇO
// ============================================================================

class PermissoesService {
  // ==========================================================================
  // TELAS
  // ==========================================================================

  /**
   * Lista todas as telas/funcionalidades do sistema
   */
  async listarTelas(modulo?: string, ativo?: boolean): Promise<Tela[]> {
    const params: Record<string, any> = {};
    if (modulo) params.modulo = modulo;
    if (ativo !== undefined) params.ativo = ativo;

    const response = await apiClient.get<Tela[]>('/telas/', { params });
    return response.data;
  }

  /**
   * Lista telas agrupadas por módulo
   */
  async listarTelasPorModulo(): Promise<Record<string, Tela[]>> {
    const telas = await this.listarTelas(undefined, true);
    return telas.reduce((acc, tela) => {
      if (!acc[tela.modulo]) {
        acc[tela.modulo] = [];
      }
      acc[tela.modulo].push(tela);
      return acc;
    }, {} as Record<string, Tela[]>);
  }

  // ==========================================================================
  // MATRIZ DE PERMISSÕES
  // ==========================================================================

  /**
   * Busca a matriz completa de permissões (todos perfis × todas telas)
   */
  async buscarMatrizPermissoes(): Promise<MatrizPermissoes> {
    const response = await apiClient.get<MatrizPermissoes>('/permissoes/matriz/');
    return response.data;
  }

  /**
   * Atualiza permissões de um perfil para uma tela específica
   */
  async atualizarPermissaoPerfil(
    perfilId: number,
    telaId: number,
    permissoes: PermissaoAtualizar
  ): Promise<PermissaoDetalhada> {
    const response = await apiClient.put<PermissaoDetalhada>(
      `/permissoes/perfil/${perfilId}/tela/${telaId}/`,
      permissoes
    );
    return response.data;
  }

  /**
   * Busca permissão específica de um perfil para uma tela
   */
  async buscarPermissaoPerfilTela(
    perfilId: number,
    telaId: number
  ): Promise<PermissaoDetalhada | null> {
    const matriz = await this.buscarMatrizPermissoes();
    return (
      matriz.matriz.find(p => p.perfil_id === perfilId && p.tela_id === telaId) || null
    );
  }

  // ==========================================================================
  // PERMISSÕES DE USUÁRIO
  // ==========================================================================

  /**
   * Busca permissões efetivas de um usuário (perfil + overrides)
   */
  async buscarPermissoesUsuario(userId: number): Promise<PermissaoEfetiva[]> {
    const response = await apiClient.get<PermissaoEfetiva[]>(`/permissoes/usuario/${userId}/`);
    return response.data;
  }

  /**
   * Cria/atualiza uma permissão específica para um usuário
   */
  async criarPermissaoUsuario(permissao: UsuarioPermissaoInput): Promise<UsuarioPermissao> {
    const response = await apiClient.post<UsuarioPermissao>('/permissoes/usuario/', permissao);
    return response.data;
  }

  /**
   * Remove uma permissão específica de um usuário (volta para permissão do perfil)
   */
  async removerPermissaoUsuario(permissaoId: number): Promise<void> {
    await apiClient.delete(`/permissoes/usuario/${permissaoId}/`);
  }

  // ==========================================================================
  // HELPERS DE PERMISSÃO
  // ==========================================================================

  /**
   * Verifica se usuário tem permissão específica em uma tela
   */
  async usuarioTemPermissao(
    userId: number,
    telaId: number,
    tipoPermissao: keyof Permissao
  ): Promise<boolean> {
    const permissoes = await this.buscarPermissoesUsuario(userId);
    const telaPermissao = permissoes.find(p => p.tela_id === telaId);
    return telaPermissao ? telaPermissao[tipoPermissao] : false;
  }

  /**
   * Verifica se usuário pode visualizar uma tela
   */
  async usuarioPodeVisualizar(userId: number, telaId: number): Promise<boolean> {
    return this.usuarioTemPermissao(userId, telaId, 'pode_visualizar');
  }

  /**
   * Filtra telas que o usuário pode visualizar
   */
  async filtrarTelasComAcesso(userId: number): Promise<Tela[]> {
    const [todasTelas, permissoes] = await Promise.all([
      this.listarTelas(undefined, true),
      this.buscarPermissoesUsuario(userId)
    ]);

    const telasComAcesso = permissoes
      .filter(p => p.pode_visualizar)
      .map(p => p.tela_id);

    return todasTelas.filter(tela => telasComAcesso.includes(tela.id));
  }

  /**
   * Agrupa permissões por módulo
   */
  async agruparPermissoesPorModulo(userId: number): Promise<Record<string, PermissaoEfetiva[]>> {
    const permissoes = await this.buscarPermissoesUsuario(userId);
    return permissoes.reduce((acc, perm) => {
      if (!acc[perm.modulo]) {
        acc[perm.modulo] = [];
      }
      acc[perm.modulo].push(perm);
      return acc;
    }, {} as Record<string, PermissaoEfetiva[]>);
  }

  /**
   * Conta total de permissões de um usuário por tipo
   */
  async contarPermissoesPorTipo(userId: number) {
    const permissoes = await this.buscarPermissoesUsuario(userId);
    return {
      visualizar: permissoes.filter(p => p.pode_visualizar).length,
      criar: permissoes.filter(p => p.pode_criar).length,
      editar: permissoes.filter(p => p.pode_editar).length,
      excluir: permissoes.filter(p => p.pode_excluir).length,
      exportar: permissoes.filter(p => p.pode_exportar).length,
      total: permissoes.length
    };
  }
}

const permissoesServiceInstance = new PermissoesService();

// Export default instance
export default permissoesServiceInstance;

// Export named function for authStore
export const buscarPermissoesUsuario = (userId: number) =>
  permissoesServiceInstance.buscarPermissoesUsuario(userId);
